import { Link, useNavigate, useLocation } from "react-router-dom";
import { Ticket, QrCode, Users, Building2, ArrowRight, ChevronDown, LogIn, Store, ShoppingCart, Gift } from "lucide-react";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Logo, LogoWordmark } from "./Logo";
import { LanguageToggle } from "./LanguageToggle";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { BUILD_LABEL } from "../lib/buildInfo";
import { useCart } from "../lib/cart";
import { checkoutUrl } from "../lib/shop";

export const PublicLayout = ({ children }) => {
  const { organizer } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const { cart, count: cartCount } = useCart();
  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Utility topbar: USP-er + arrangørlenker */}
      <div className="bg-accent/50 border-b border-border" data-testid="utility-topbar">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-9 flex items-center justify-between gap-4 text-xs text-muted-foreground">
          <div className="flex items-center gap-4 sm:gap-6 min-w-0" data-testid="topbar-usp-row">
            <span className="flex items-center gap-1.5 whitespace-nowrap"><Ticket className="w-3.5 h-3.5 text-brand" /> {t("Ikke gebyrpåslag")}</span>
            <span className="hidden sm:flex items-center gap-1.5 whitespace-nowrap"><QrCode className="w-3.5 h-3.5 text-brand" /> {t("QR-kode innsjekk")}</span>
            <span className="hidden md:flex items-center gap-1.5 whitespace-nowrap"><Users className="w-3.5 h-3.5 text-brand" /> {t("Ingen log-in nødvendig")}</span>
          </div>
          {!organizer && (
            <p className="whitespace-nowrap" data-testid="topbar-organizer-line">
              {t("Er du arrangør?")}{" "}
              <Link to="/logg-inn" className="underline underline-offset-4 hover:text-foreground transition-colors duration-150" data-testid="topbar-login-link">
                {t("Logg inn")}
              </Link>
              {" "}{t("eller")}{" "}
              <Link to="/for-arrangorer" className="underline underline-offset-4 hover:text-foreground transition-colors duration-150" data-testid="topbar-register-link">
                {t("bli arrangør")}
              </Link>
            </p>
          )}
        </div>
      </div>
      <header className="sticky top-0 z-40 bg-card border-b border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-2">
          <Link to="/" className="flex items-center min-w-0" data-testid="nav-logo-link">
            <Logo showTagline taglineClassName="hidden sm:inline" />
          </Link>
          <nav className="flex items-center gap-1 sm:gap-2 shrink-0">
            {/* Butikk (tilleggsmodul): felles marknadsplass for varer frå arrangørane */}
            <Button
              asChild
              variant={location.pathname.startsWith("/butikk") ? "secondary" : "ghost"}
              size="sm"
              className={`px-2.5 ${cartCount > 0 ? "hidden sm:inline-flex" : ""}`}
              data-testid="nav-shop-link"
            >
              <Link to="/butikk" aria-label={t("Butikk")} title={t("Butikk")}>
                <Store className="w-4 h-4 sm:mr-1.5" />
                <span className="hidden sm:inline">{t("Butikk")}</span>
              </Link>
            </Button>
            {/* Basar (tilleggsmodul): felles oversikt over loddsal frå arrangørane */}
            <Button
              asChild
              variant={location.pathname.startsWith("/basar") ? "secondary" : "ghost"}
              size="sm"
              className={`px-2.5 ${cartCount > 0 ? "hidden sm:inline-flex" : ""}`}
              data-testid="nav-basar-link"
            >
              <Link to="/basar" aria-label={t("Basar")} title={t("Basar")}>
                <Gift className="w-4 h-4 sm:mr-1.5" />
                <span className="hidden sm:inline">{t("Basar")}</span>
              </Link>
            </Button>
            {/* Handlekorg (éi per arrangør) – vist berre når det ligg noko i korga */}
            {cartCount > 0 && (
              <Button asChild variant="ghost" size="sm" className="px-2.5 relative" data-testid="nav-cart-link">
                <Link to={checkoutUrl(cart.organizer_id)} aria-label={t("Handlekurv ({n})", { n: cartCount })} title={t("Handlekurv ({n})", { n: cartCount })}>
                  <ShoppingCart className="w-4 h-4" />
                  <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[hsl(var(--brand))] text-[hsl(var(--brand-foreground))] text-[10px] font-semibold inline-flex items-center justify-center tabular-nums" data-testid="nav-cart-count">{cartCount}</span>
                </Link>
              </Button>
            )}
            <LanguageToggle />
            {organizer ? (
              <Button asChild variant="default" size="sm" data-testid="nav-dashboard-button">
                <Link to="/dashboard">{t("Dashbord")}</Link>
              </Button>
            ) : (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="default" size="sm" className="bg-brand text-brand-foreground hover:bg-brand/90 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 px-2.5 sm:px-3" data-testid="nav-organizer-menu-button">
                    <Building2 className="w-4 h-4 mr-1.5" />
                    {t("Arrangør")}
                    <ChevronDown className="w-4 h-4 ml-1.5 hidden min-[420px]:inline-block" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem
                    onClick={() => navigate("/logg-inn")}
                    className="cursor-pointer"
                    data-testid="nav-organizer-login-item"
                  >
                    <LogIn className="w-4 h-4 mr-2" />
                    {t("Logg inn")}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => navigate("/for-arrangorer")}
                    className="cursor-pointer"
                    data-testid="nav-organizer-register-item"
                  >
                    <ArrowRight className="w-4 h-4 mr-2" />
                    {t("Bli arrangør")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </nav>
        </div>
      </header>
      <main className="flex-1">{children}</main>
      <footer className="bg-ink text-ink-foreground mt-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex flex-col gap-1">
            <LogoWordmark light />
            <span className="text-xs text-primary-foreground/70">Enklere billettering · Simplified ticketing</span>
          </div>
          <div className="flex flex-col items-start sm:items-end gap-1">
            <p className="text-sm opacity-80">{t("rightin.no — Ikke gebyrpåslag – QR-innsjekk inkludert.")}</p>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 sm:justify-end">
              <Link to="/for-arrangorer" className="text-sm underline underline-offset-4 opacity-80 hover:opacity-100 transition-opacity duration-150" data-testid="footer-for-organizers-link">
                {t("For arrangører")}
              </Link>
              <Link to="/butikk" className="text-sm underline underline-offset-4 opacity-80 hover:opacity-100 transition-opacity duration-150" data-testid="footer-shop-link">
                {t("Butikk")}
              </Link>
              <Link to="/basar" className="text-sm underline underline-offset-4 opacity-80 hover:opacity-100 transition-opacity duration-150" data-testid="footer-basar-link">
                {t("Basar")}
              </Link>
              <Link to="/pris" className="text-sm underline underline-offset-4 opacity-80 hover:opacity-100 transition-opacity duration-150" data-testid="footer-pricing-link">
                {t("Pris")}
              </Link>
              <Link to="/nyheter" className="text-sm underline underline-offset-4 opacity-80 hover:opacity-100 transition-opacity duration-150" data-testid="footer-changelog-link">
                {t("Hva er nytt")}
              </Link>
              <Link to="/nyhetsbrev" className="text-sm underline underline-offset-4 opacity-80 hover:opacity-100 transition-opacity duration-150" data-testid="footer-newsletter-link">
                {t("Nyhetsbrev")}
              </Link>
              <Link to="/vilkar" className="text-sm underline underline-offset-4 opacity-80 hover:opacity-100 transition-opacity duration-150" data-testid="footer-terms-link">
                {t("Vilkår for kjøp")}
              </Link>
              <Link to="/personvern" className="text-sm underline underline-offset-4 opacity-80 hover:opacity-100 transition-opacity duration-150" data-testid="footer-privacy-link">
                {t("Personvern og GDPR")}
              </Link>
              <Link to="/om-oss" className="text-sm underline underline-offset-4 opacity-80 hover:opacity-100 transition-opacity duration-150" data-testid="footer-about-link">
                {t("Om oss")}
              </Link>
              <Link to="/hjelp" className="text-sm underline underline-offset-4 opacity-80 hover:opacity-100 transition-opacity duration-150" data-testid="footer-help-link">
                {t("Slik henter du billett")}
              </Link>
            </div>
          </div>
        </div>
        <div className="border-t border-primary-foreground/15">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-1 text-xs text-primary-foreground/60">
            <span data-testid="footer-copyright">
              © {new Date().getFullYear()} Right in · {t("Alle rettigheter forbeholdt")}
            </span>
            <span className="flex items-center gap-3">
              <Link to="/vilkar" className="underline underline-offset-4 hover:text-primary-foreground transition-colors duration-150" data-testid="footer-terms-bottom-link">
                {t("Vilkår for kjøp")}
              </Link>
              <Link to="/personvern" className="underline underline-offset-4 hover:text-primary-foreground transition-colors duration-150" data-testid="footer-privacy-bottom-link">
                {t("Personvernerklæring (GDPR)")}
              </Link>
              <Link to="/om-oss" className="underline underline-offset-4 hover:text-primary-foreground transition-colors duration-150" data-testid="footer-about-bottom-link">
                {t("Om oss")}
              </Link>
              <Link to="/hjelp" className="underline underline-offset-4 hover:text-primary-foreground transition-colors duration-150" data-testid="footer-help-bottom-link">
                {t("Hjelp")}
              </Link>
            </span>
          </div>
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-3 -mt-1">
            <p className="font-mono text-[11px] tracking-wide text-primary-foreground/45" data-testid="footer-build-label">
              {BUILD_LABEL}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};
